<script setup lang="ts">
import { api } from '@/api';
import {
  ISingleSystemSetupParameter,
  QaEquipmentRtSystemDto,
  QaRtSystemTemplateContainerDto,
  TaskItemQaEquipmentSetupDto,
  TaskTypeDescription,
} from '@/api/generator';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { edit } from '@/components/icons/icons.ts';
import RTSystemPairRow from '@/components/RTSystemTile/RTSystemPairRow.vue';
import RTSystemTile from '@/components/RTSystemTile/RTSystemTile.vue';
import { useToggle } from '@vueuse/core';
import { useField, useFieldArray, useSetFieldValue } from 'vee-validate';
import { computed, onMounted, ref, toRef } from 'vue';
import { toCreateTaskItemQaEuipmentSetupDto } from '../mapping';
import FormAddButton from './FormAddButton.vue';
import QaSectionGroupParameters from './QaSectionGroupParameters.vue';
import QaSectionSystemSelection from './QaSectionSystemSelection.vue';
import {
  isBeamAnalysisPair,
  loadQaRtSystemTemplateContainers,
  orderSystemsForBeamAnalysis,
} from './QaSectionUtil';
import QaSetupParameterAddDialog from './QaSetupParameterAddDialog.vue';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import InlineValidationError from '@/components/forms/InlineValidationError/InlineValidationError.vue';
import { allowsCustomSetupParameters, usesSingleQaEquipmentGroup } from './setupParameterRules';

const props = defineProps<{
  name: string; // taskItemAreas[{areaIndex}].qaEquipmentSetups[{groupIndex}]
  taskType: TaskTypeDescription;
  alreadyUsedIDs?: string[];
  alreadyUsedGroupNames?: string[];
  /** Whether the group may be deleted; the section decides this from the group count. */
  removable?: boolean;
}>();

// Single group task types have no group name, so their card has no title bar.
const isSingleGroup = computed(() => usesSingleQaEquipmentGroup(props.taskType));

const alreadyUsedIDsOutsideGroup = computed(() => {
  const usedIdsInGroup = displayQaEquipmentRtSystemDtos.value.map((dto) => dto.id);
  return props.alreadyUsedIDs?.filter((id) => !usedIdsInGroup.includes(id));
});

const alreadyUsedGroupNamesOutsideGroup = computed(() => {
  return props.alreadyUsedGroupNames?.filter((gn) => taskItemQaEquipmentSetupDto.value.name !== gn);
});

// vee validate
const { value: taskItemQaEquipmentSetupDto } = useField<TaskItemQaEquipmentSetupDto>(
  toRef(() => `${props.name}`),
);

const { replace: replaceSingleSetupParameters, fields: qaParameterValues } =
  useFieldArray<ISingleSystemSetupParameter>(toRef(() => `${props.name}.singleSetupParameters`));

const { value: supportSystemEntityIds, setValue: replaceSupportSystemIDsArray } = useField<
  string[]
>(toRef(() => `${props.name}.supportSystemEntityIds`));

const setGroupName = useSetFieldValue<string>(toRef(() => `${props.name}.name`));

const emits = defineEmits<{
  removeItem: [];
}>();

const handleRemove = () => {
  emits('removeItem');
};

const mode = useFormMode();

const qaRtSystemTemplateContainerDtos = ref<QaRtSystemTemplateContainerDto[] | undefined | null>(
  undefined,
);

const getSupportSystemForId = (id: string): QaEquipmentRtSystemDto | undefined => {
  return qaRtSystemTemplateContainerDtos.value
    ?.flatMap((item) => item.entries || [])
    .map((innerItem) => innerItem.system)
    .find((system) => system?.id === id);
};

const displayQaEquipmentRtSystemDtos = computed<QaEquipmentRtSystemDto[]>(() => {
  const items = supportSystemEntityIds.value?.map((id) => getSupportSystemForId(id)) ?? [];
  const systems = items.filter((item): item is QaEquipmentRtSystemDto => item !== undefined);
  return props.taskType === 'BeamAnalysis' ? orderSystemsForBeamAnalysis(systems) : systems;
});

// Beam analysis: the interface (slot 0) and array (slot 1) are shown as one
// connected pair in a centered row of their own, joined by a bridge spanning
// the gap between their tiles. The remaining equipment follows in the grid.
// Without a complete pair everything renders in the plain grid.
const showBeamAnalysisPairRow = computed(
  () =>
    props.taskType === 'BeamAnalysis' && isBeamAnalysisPair(displayQaEquipmentRtSystemDtos.value),
);

const pairRowSystems = computed(() =>
  showBeamAnalysisPairRow.value ? displayQaEquipmentRtSystemDtos.value.slice(0, 2) : [],
);

const gridSystems = computed(() =>
  showBeamAnalysisPairRow.value
    ? displayQaEquipmentRtSystemDtos.value.slice(2)
    : displayQaEquipmentRtSystemDtos.value,
);

// modal states
const editModalOpen = ref(false);
const [isQaParameterDialogOpen, toggleQaParameterDialog] = useToggle(false);

async function handleSupportSystemSetupUpdate(supportSystemIds: string[], groupName: string) {
  editModalOpen.value = false;

  const { result } = await api.taskItemsApi.getTaskItemQaEquipmentSetupTemplate({
    getQaEquipmentSetupTemplateDto: {
      taskType: props.taskType,
      supportSystemEntityIds: supportSystemIds,
      previousQaEquipmentSetup:
        taskItemQaEquipmentSetupDto.value && taskItemQaEquipmentSetupDto.value.singleSetupParameters
          ? toCreateTaskItemQaEuipmentSetupDto(taskItemQaEquipmentSetupDto.value)
          : null,
    },
  });

  if (result?.singleSetupParameters) {
    replaceSingleSetupParameters(result.singleSetupParameters);
  }

  setGroupName(groupName);
  replaceSupportSystemIDsArray(result?.supportSystemEntityIds ?? [], true);
}

onMounted(() => {
  loadQaRtSystemTemplateContainers(
    props.taskType,
    (result) => (qaRtSystemTemplateContainerDtos.value = result),
    taskItemQaEquipmentSetupDto.value?.supportSystemEntityIds || [],
  );
});

const checkIfParameterMightGoMissingAlertShouldBeDisplayedInQaSectionModal = () => {
  const enabledParameters = qaParameterValues.value
    .filter((item) => item.value.isEnabled)
    .map((item) => item.value);

  return enabledParameters?.length;
};

// Computed property for all parameters (for the select button)
const showSelectSetupParameterButton = computed(
  () => qaParameterValues.value.map((item) => item.value).length,
);
</script>

<template>
  <div>
    <InlineValidationError class="inline-error dense mt-3 mb-3" ignoreTouched :name="name" />

    <InlineValidationError
      class="inline-error dense mt-3 mb-3"
      ignoreTouched
      validateOnMount
      :name="`${name}.supportSystemEntityIds`"
    />

    <v-card
      class="container-level-2"
      :class="{
        'overflow-visible': isSingleGroup,
        'task-type-manual': !isSingleGroup,
        'task-type-single-group': isSingleGroup,
      }"
    >
      <v-card-item>
        <v-card-title>
          <h5>
            {{ taskItemQaEquipmentSetupDto?.name }}
          </h5>
          <VSpacer />

          <TextVBtn
            v-if="mode !== 'details'"
            :icon="edit"
            size="small"
            @click="() => (editModalOpen = true)"
          />

          <!-- remove button -->
          <PtwDeleteButton v-if="mode !== 'details' && removable" @delete="handleRemove" />
        </v-card-title>
      </v-card-item>

      <v-card-text>
        <!-- beam analysis: interface + array pair in a row of their own -->
        <RTSystemPairRow
          v-if="showBeamAnalysisPairRow"
          :systems="pairRowSystems"
          :dontShowDeviceState="true"
          class="tile-pair-row"
        />

        <!-- tiles -->
        <div v-if="gridSystems.length" class="tile-container mb-6">
          <RTSystemTile
            v-for="gridSystem in gridSystems"
            :key="gridSystem.id"
            :item="gridSystem"
            :dontShowDeviceState="true"
          />
        </div>

        <!-- add parameter button -- manual task only -->
        <v-card class="container-level-3">
          <v-card-item>
            <v-card-title>
              <h6>{{ $t('SetupParameters') }}</h6>
              <VSpacer />
              <FormAddButton
                v-if="
                  mode !== 'details' &&
                  allowsCustomSetupParameters(taskType) &&
                  showSelectSetupParameterButton
                "
                :label="'AddSetupParameter'"
                @click="toggleQaParameterDialog(true)"
              />
            </v-card-title>
          </v-card-item>
          <v-card-text>
            <!-- QA parameters -->
            <QaSectionGroupParameters :name="`${name}.singleSetupParameters`" :taskType="taskType"
          /></v-card-text>
        </v-card>
      </v-card-text>
    </v-card>

    <!-- add parameter dialog -->
    <QaSetupParameterAddDialog
      v-model="isQaParameterDialogOpen"
      :taskType="taskType"
      :parameters="qaParameterValues.map((item) => item.value)"
      @addParameters="
        (r) => {
          replaceSingleSetupParameters(r);
        }
      "
    />

    <!-- edit dialog  -->
    <QaSectionSystemSelection
      v-model="editModalOpen"
      :name="name"
      :taskType="taskType"
      :qaRtSystemTemplateContainerDtos="qaRtSystemTemplateContainerDtos || []"
      :groupName="taskItemQaEquipmentSetupDto?.name || ''"
      :alreadyUsedIDs="alreadyUsedIDsOutsideGroup"
      :alreadyUsedGroupNames="alreadyUsedGroupNamesOutsideGroup"
      :supportSystemEntityIds="taskItemQaEquipmentSetupDto?.supportSystemEntityIds"
      :notification="
        checkIfParameterMightGoMissingAlertShouldBeDisplayedInQaSectionModal()
          ? {
              type: 'warning',
              message: $t('SetupParametersMightGoMissing'),
            }
          : undefined
      "
      @submit="handleSupportSystemSetupUpdate"
    />
  </div>
</template>

<style lang="scss" scoped>
.v-card {
  &.content-block {
    &.task-type-single-group {
      .v-card-item {
        .v-card-title {
          background-color: transparent;
        }
      }

      .v-card-text {
        padding: 0 !important;
      }
    }
    > .v-card-item .v-card-title {
      background: rgb(var(--v-theme-primary-lighten-5)) !important;
      padding: 4px 16px;
    }
  }
}
$tile-column-gap: 16px;
$tile-row-gap: 8px;

.v-card .tile-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: $tile-row-gap $tile-column-gap;
}

// The pair is a section of its own: the same 24px that separates the tiles
// from the setup parameters card also separates the pair from the tiles below.
.v-card .tile-pair-row {
  margin-bottom: 24px;
}
</style>
